/**
 * React Query hooks for Map Editor Projects API
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { mapEditorService } from '../api';
import type {
  CreateProjectRequest,
  UpdateProjectRequest,
} from '../api/types';

// Query Keys
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (siteProjectId?: number) =>
    siteProjectId ? [...projectKeys.lists(), siteProjectId] as const : projectKeys.lists(),
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: number) => [...projectKeys.details(), id] as const,
};

/**
 * 프로젝트 목록 조회
 */
export function useProjects(siteProjectId: number) {
  return useQuery({
    queryKey: projectKeys.list(siteProjectId),
    queryFn: async () => {
      const response = await mapEditorService.getProjects(siteProjectId);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: !!siteProjectId, // siteProjectId가 있을 때만 쿼리 실행
  });
}

/**
 * 프로젝트 생성
 */
export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateProjectRequest) => {
      const response = await mapEditorService.createProject(data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      // 프로젝트 목록 새로고침
      queryClient.invalidateQueries({ queryKey: projectKeys.list() });
    },
  });
}

/**
 * 프로젝트 수정
 */
export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: number;
      data: UpdateProjectRequest;
    }) => {
      const response = await mapEditorService.updateProject(id, data);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: (_, variables) => {
      // 프로젝트 목록과 상세 정보 새로고침
      queryClient.invalidateQueries({ queryKey: projectKeys.list() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
    },
  });
}

/**
 * 프로젝트 삭제
 */
export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: number) => {
      const response = await mapEditorService.deleteProject(id);
      if (!response.success) {
        throw new Error(response.message);
      }
      return response.data;
    },
    onSuccess: () => {
      // 프로젝트 목록 새로고침
      queryClient.invalidateQueries({ queryKey: projectKeys.list() });
    },
  });
}
